import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { History, TrendingDown, Minus } from 'lucide-react';
import { useCurrency } from './CurrencySelector';
import { usePageT } from '../i18n/usePageT';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PriceHistoryChart = ({ productId }) => {
  const T = usePageT();
  const { format } = useCurrency();
  const [history, setHistory] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products/${productId}/price-history`)
      .then((r) => setHistory(r.data))
      .catch(() => setHistory([]));
  }, [productId]);

  if (!history || history.length === 0) return null;

  const prices = history.map((h) => h.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const last = prices[prices.length - 1];
  const dropPct = max > 0 ? Math.round((1 - last / max) * 100) : 0;
  const stable = max - min < 0.01;

  const W = 260;
  const H = 48;
  const pts = prices.length === 1 ? [prices[0], prices[0]] : prices;
  const span = Math.max(max - min, 0.01);
  const points = pts
    .map((p, i) => `${((i / (pts.length - 1)) * (W - 4) + 2).toFixed(1)},${(H - 6 - ((p - min) / span) * (H - 12) + 3).toFixed(1)}`)
    .join(' ');

  return (
    <div className="border border-[#0F4C5C]/10 bg-white rounded-xl p-4 max-w-md" data-testid="price-history-chart">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-[#0F4C5C] flex items-center gap-1.5">
          <History className="w-4 h-4" />
          {T('Historique du prix', 'Price history')}
        </p>
        {stable ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5" data-testid="price-history-trend">
            <Minus className="w-3 h-3" />
            {T('Prix stable', 'Stable price')}
          </span>
        ) : dropPct > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 rounded-full px-2 py-0.5" data-testid="price-history-trend">
            <TrendingDown className="w-3 h-3" />
            {T('-{pct}% vs le plus haut', '-{pct}% vs highest').replace('{pct}', dropPct)}
          </span>
        ) : null}
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="text-[#2A9D8F]" preserveAspectRatio="none" style={{ height: 48 }}>
        <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={points.split(' ').pop().split(',')[0]} cy={points.split(' ').pop().split(',')[1]} r="3" fill="currentColor" />
      </svg>
      <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
        <span>{T('Plus bas', 'Lowest')} : <strong className="text-[#0F4C5C]">{format(min)}</strong></span>
        <span>{T('Plus haut', 'Highest')} : <strong className="text-[#0F4C5C]">{format(max)}</strong></span>
      </div>
    </div>
  );
};
